import axios, { isAxiosError } from 'axios'
import { defineStore } from 'pinia'
import { camelize } from 'camelize-snakeize-ts'
import dashboardApi from '@/helpers/dashboardApi'
import api from '@/helpers/api'
import paymentsApi from '@/helpers/paymentsApi'
import jwtToken from '@/helpers/getJwtToken'
import { removeNullableObjectFields } from '@/helpers/requestData'
import { SocietyTypes, SubscriptionTypes } from '@/constants'

const API_URL = process.env.VUE_APP_API_URL

interface UserStore {
  user: User,
  userDetails: UserDetails | null,
  linkedAccounts: LinkedAccount[],
}

export const useUserStore = defineStore('user', {
  state: () => ({
    user: {},
    userDetails: null,
    linkedAccounts: [],
  }) as UserStore,

  getters: {
    isRlsSubscription: state => (): boolean => (
      state.user.subscription?.type === SubscriptionTypes.rls
    ),

    hasProOrLabelSubscription: state => (
      state.user.subscription
        ? Boolean(state.user.subscription.subscriptionPlan.artistLimit > 1)
        : false
    ),

    hasStarterSubscription: state => (
      state.user.subscription
        ? state.user.subscription.subscriptionPlan.artistLimit === 1
        : false
    ),

    getLinkedUserById: state => (id: number): LinkedAccount | undefined => state.linkedAccounts.find(user => user.id === id),

    getLinkedUserEmailByName: state => (name: string) => {
      const linkedUser = state.linkedAccounts.find(user => user.name === name)

      return (linkedUser && linkedUser.email) || ''
    },

    mechanicalRightsSocieties(state): Society[] | undefined {
      const mechanicalRightsSocieties = state.userDetails?.societies.filter(
        ({ type }) => type === SocietyTypes.mechanical || type === SocietyTypes.universal,
      )

      return mechanicalRightsSocieties?.sort((a, b) => a.acronym.localeCompare(b.acronym))
    },

    performingRightsSocieties(state): Society[] | undefined {
      const performingRightsSocieties = state.userDetails?.societies.filter(
        ({ type }) => type === SocietyTypes.performing || type === SocietyTypes.universal,
      )

      return performingRightsSocieties?.sort((a, b) => a.acronym.localeCompare(b.acronym))
    },
  },

  actions: {
    async getJwtUser() {
      const { data: { id, countryCode, name, email } } = await dashboardApi.get('api/me')

      this.$patch({ user: {
        id,
        countryCode,
        name,
        email,
      } })
    },

    async getUserSubscription() {
      try {
        const { data } = await paymentsApi.get('/me/subscription', {
          params: {
            expand: true,
          },
        })

        this.$patch({ user: {
          subscription: <Subscription>camelize(data),
        } })
      } catch (error) {
        if (isAxiosError(error) && error.response && error.response.status !== 404) {
          throw error
        }
      }
    },

    async getLinkedAccounts() {
      const { data } = await axios.get(`${API_URL}/api/publishing/user/linked_accounts`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${jwtToken}`,
        },
      })

      this.$patch({
        linkedAccounts: data,
      })
    },

    async getUserDetails(userId: number) {
      const { data } = await api.get(`/publishing/user/${userId}/details`)

      this.$patch({
        userDetails: data,
      })
    },

    async updateUserDetails(data: UserDetailsForm) {
      await api.post(
        `publishing/user/${this.user?.id}/update-details`,
        { userDetails: removeNullableObjectFields(data.userDetails),
        },
      )

      this.$patch({
        userDetails: {
          ...this.userDetails,
          ...data.userDetails,
          isReviewDetailsDisplayable: false,
        },
        user: {
          name: `${data.userDetails.firstname} ${data.userDetails.lastname}`,
        },
      })
    },
  },
})
